import cloneFn from 'lodash.clonedeep';
import mutations from './mutations';
import actions from './actions';

const defaultState = () => {
  return {
    // Cached data - ofline cache, store raw and prestructured
    cache: {},

    // If cache is warmed up
    cached: false
  };
};

const Tags = {
  namespaced: true,
  state: defaultState,
  getters: {
    cached: (state) => state.cached,

    tagsTree: (state) => state.tagsTree,

    cachedTagHash: (state) => state.cache.hash,

    cachedTagHasChildren: (state, getters) => ({ id }) => getters.cached && Object.prototype.hasOwnProperty.call(state.cache.childs, id),

    lookupTagById: (state, getters) => ({ id, clone /* default is true */ }) => {
      const results = getters.lookupTagsByIds({ ids: [id], clone });
      return Object.prototype.hasOwnProperty.call(results, id) ? results[id] : null;
    },

    lookupTagsByIds: (state, getters) => ({ ids, clone /* default is true */ }) => {
      const acum = {};
      const idsFound = ids.filter((id) => getters.cached && Object.prototype.hasOwnProperty.call(state.cache.idx, id));
      idsFound.forEach((id) => {
        acum[id] = clone !== false
          ? cloneFn(state.cache.list[state.cache.idx[id]])
          : state.cache.list[state.cache.idx[id]];
      });
      return acum;
    },
    /**
     *
     * @param {String} query String to search for
     * if empty or empty array [], then do not fillter based on subtypes
     * @param {Boolean} clone pass false to skip deep cloning return value, default true
     */
    searchTags: (state, getters) => (
      {
        query, customFilter, mergeWithInfo, clone /* default is true */ }
    ) => {
      return !getters.cached ? [] // Actual search logic below in 2 regexp`s
        : state.cache.list.filter(
          (item) => (!customFilter || customFilter({ item }))
            && (!query
                || query === ''
                || !item.label
                || (item.label.search(new RegExp(query, 'i')) !== -1)
                || (!Number.isNaN(+query) ? (item.id.toString().search(new RegExp(parseInt(query, 10), 'i')) !== -1) : false)
                // || (item.code && item.code.search(new RegExp(`^${query}`, 'i')) !== -1)
            )
        )
          .map((item) => (clone !== false ? cloneFn(item) : item))
          .map((item) => { return { ...item, searchMatch: true }; });
    },

    /**
     *
     * @param {String} query String to search for
     * if empty or empty array [], then do not fillter based on subtypes
     * @param {Boolean} fillChildren fills children under matched search results, marks them as searchChild:true
     * @param {Boolean} clone pass false to skip deep cloning return value, default true
     */
    searchTagsTree: (state, getters, rootState, rootGetters) => (
      {
        query, customFilter, fillChildren, mergeWithInfo, clone /* default is true */
      }
    ) => {
      const currentProjectTags = rootGetters['Project/currentProjectTags'];
      const resultsFlat = getters.searchTags({
        query, clone, customFilter,
      });
      // Points to items inserted in tree, as they ar objects they are treated as references
      const treeIdxRef = {
        0: { children: [] } // result will be children of item with id 0;
      };
      resultsFlat.forEach((item) => {
        item.pathArr.forEach((parentId) => {
          // If item is in completed tree - skip
          if (Object.prototype.hasOwnProperty.call(treeIdxRef, parentId)) return;

          // Find in search results, if not found, then fin in full tree
          const insertable = resultsFlat.find((e) => e.id === parentId)
            || getters.lookupTagById({ id: parentId });
          if (!insertable || !Object.prototype.hasOwnProperty.call(treeIdxRef, insertable.parent_id || 0)) {
            console.warn('Unexpected condition rebuilding tree array!', insertable ? insertable.parent_id || 0 : null);
            return;
          }
          // insert in results
          insertable.children = false;
          // insertable.isDefaultExpanded = query && query.length > 0;
          if (treeIdxRef[insertable.parent_id || 0].children === false) {
            treeIdxRef[insertable.parent_id || 0].children = [];
          }
          treeIdxRef[insertable.parent_id || 0].children.push(insertable);
          treeIdxRef[parentId] = insertable;
        });
      });

      if ((!fillChildren || !query || query.length === 0) && !mergeWithInfo) return treeIdxRef[0].children;
      // Fill children under search results
      let childrenRequired = [...resultsFlat];
      while (childrenRequired.length > 0) {
        const tItem = childrenRequired.pop();
        const directChildren = getters.getSingleLevelChildren({ id: tItem.id, clone })
        // TODO marks anly added items as searchChild, could add to all children if map() performed later
          .map((item) => { item.searchChild = true; return item; });
        const hasChildren = directChildren && directChildren.length > 0;
        // Only append children that were not loaded
        tItem.children = hasChildren
          ? (tItem.children ? tItem.children : []).concat(directChildren.filter((item) => !tItem.children
            || !tItem.children.find((existing) => existing.id === item.id))) : false;
        childrenRequired = childrenRequired.concat(hasChildren ? directChildren : []);
      }
      return treeIdxRef[0].children.map((t) => {
        if (currentProjectTags !== undefined) {
          t.pinfo = currentProjectTags.find((it) => t.id === it.tag_id) ? currentProjectTags.find((it) => t.id === it.tag_id) : null;
        }
        return t;
      });
    },

    /**
     *
     * @param {Array} types Array of types where to lookup parent id
     * @param {Number} id Parent id of children to lookup  ( default is 0 - ROOT )
     * @param {Boolean} clone pass false to skip deep cloning return value, default true
     */
    getSingleLevelChildren: (state, getters) => (
      { id, clone /* default is true */ }
    ) => {
      id = id || 0;
      return (getters.cachedTagHasChildren({ id })
        ? Array.from(state.cache.childs[id], (childId) => {
          return {
            ...(clone !== false
              ? cloneFn(state.cache.list[state.cache.idx[childId]])
              : state.cache.list[state.cache.idx[childId]]),
            children: getters.cachedTagHasChildren({ id: childId }) ? null : false
          };
        })
        : []);
    },

    /**
     *
     * @param {Number} id Parent id of children to lookup ( default is 0 - ROOT )
     * @param {Boolean} clone pass false to skip deep cloning return value, default true
     */
    getAllChildrenTree: (state, getters) => ({ id, clone /* default is true */ }) => {
      id = id || 0;
      const result = getters.getSingleLevelChildren({ id, clone });
      let childrenRequired = [...result];
      while (childrenRequired.length > 0) {
        const tItem = childrenRequired.pop();
        const directChildren = getters.getSingleLevelChildren({ id: tItem.id, clone });
        /* @TODO could assign  null if error or unloaded children */
        const hasChildren = directChildren && directChildren.length > 0;
        tItem.children = hasChildren ? directChildren : false;
        childrenRequired = childrenRequired.concat(hasChildren ? directChildren : []);
      }
      return result;
    }
  },
  mutations,
  actions,
};

export { Tags, defaultState };
